/** Read-only viewer for a stored document — import inputs, webhook bodies,
 *  upstream responses.
 *
 * Owns the whole toolbar: detected format + size, a Tree/Text switch for JSON,
 * Pretty-print, Wrap lines, Copy and Download. The format is sniffed from the
 * text itself (`detectFormat`); `formatHint` only breaks ties for documents
 * that start with neither `<` nor `{`/`[`.
 *
 * Pretty-print defaults to ON (these payloads arrive minified far more often
 * than not) except for documents above `PRETTY_AUTO_MAX`, where a synchronous
 * reformat would stall the UI. **Copy and Download always emit the stored
 * bytes**, never the reformatted text: they are the forensic artifact, and
 * reformatted XML must not be round-tripped back into a reimport.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';

import { JsonEditorWidget } from '@/components/JsonEditor/JsonEditorWidget';
import { useSnackbar } from '@/components/Snackbar';
import { detectFormat, prettyPrint, PRETTY_AUTO_MAX, type DocFormat } from '@/utils/prettyPrint';

import { CodeViewer } from './CodeViewer';

const EXT: Record<DocFormat, string> = { xml: 'xml', json: 'json', text: 'txt' };

function downloadText(name: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export interface DocumentViewerProps {
  /** The stored document, exactly as persisted. */
  text: string;
  /** Server-reported format — consulted only when sniffing is inconclusive. */
  formatHint?: string;
  /** Download file name without extension; the extension follows the format. */
  downloadName: string;
  /** Snackbar confirmation after Copy. */
  copyMessage: string;
  /** Rendered in place of the viewer when `text` is empty. */
  emptyMessage: string;
  /** Extra toolbar content, before the format/size caption. */
  leading?: ReactNode;
  /** Rendered between the toolbar and the viewer. */
  beforeViewer?: ReactNode;
}

export function DocumentViewer({
  text,
  formatHint,
  downloadName,
  copyMessage,
  emptyMessage,
  leading,
  beforeViewer,
}: DocumentViewerProps) {
  const snackbar = useSnackbar();
  const [wrap, setWrap] = useState(false);
  const [jsonView, setJsonView] = useState<'tree' | 'text'>('tree');
  /** null = follow the size-based default. */
  const [pretty, setPretty] = useState<boolean | null>(null);

  const format = detectFormat(text, formatHint);

  const parsedJson = useMemo(() => {
    if (format !== 'json' || !text) return null;
    try {
      return JSON.parse(text) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [format, text]);

  const showTree = parsedJson !== null && jsonView === 'tree';
  const isLarge = text.length > PRETTY_AUTO_MAX;
  const canPretty = (format === 'xml' || format === 'json') && !showTree;
  const prettyOn = canPretty && (pretty ?? !isLarge);

  const formatted = useMemo(
    () => (prettyOn ? prettyPrint(text, format) : null),
    [prettyOn, text, format],
  );

  if (!text) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, flex: 1 }}>
        {leading != null && (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {leading}
          </Stack>
        )}
        {beforeViewer}
        <Alert severity="info">{emptyMessage}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, flex: 1 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {leading}
        <Typography variant="body2" color="text.secondary">
          {format.toUpperCase()} · {(text.length / 1024).toFixed(1)} KB
        </Typography>
        {parsedJson !== null && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={jsonView}
            onChange={(_, v: 'tree' | 'text' | null) => v && setJsonView(v)}
            aria-label="JSON view"
          >
            <ToggleButton value="tree">Tree</ToggleButton>
            <ToggleButton value="text">Text</ToggleButton>
          </ToggleButtonGroup>
        )}
        {canPretty && (
          <Tooltip
            title={
              isLarge
                ? 'Large document — pretty-printing is off by default because reformatting blocks the UI.'
                : ''
            }
          >
            <FormControlLabel
              control={
                <Switch size="small" checked={prettyOn} onChange={(e) => setPretty(e.target.checked)} />
              }
              label="Pretty-print"
              slotProps={{ typography: { variant: 'body2' } }}
            />
          </Tooltip>
        )}
        {prettyOn && formatted === null && (
          <Typography variant="caption" color="warning.main">
            Couldn&apos;t pretty-print — this isn&apos;t well-formed {format.toUpperCase()}.
          </Typography>
        )}
        {!showTree && (
          <FormControlLabel
            control={<Switch size="small" checked={wrap} onChange={(e) => setWrap(e.target.checked)} />}
            label="Wrap lines"
            slotProps={{ typography: { variant: 'body2' } }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={() => {
            void navigator.clipboard.writeText(text);
            snackbar.show(copyMessage, 'success');
          }}
        >
          Copy
        </Button>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => downloadText(`${downloadName}.${EXT[format]}`, text)}
        >
          Download
        </Button>
      </Stack>

      {beforeViewer}

      {showTree ? (
        <JsonEditorWidget initialValue={parsedJson} readOnly mode="code" />
      ) : (
        <CodeViewer
          value={formatted ?? text}
          language={format === 'xml' ? 'xml' : 'none'}
          wrap={wrap}
          maxHeight="100%"
        />
      )}
    </Box>
  );
}
